import { useEffect, useRef } from 'react'
import type { DiffOverviewMarker } from '@/lib/diff-overview-markers'
import { testIds } from '@/lib/test-ids'

const MARKER_COLORS: Record<DiffOverviewMarker['type'], string> = {
  added: 'rgb(34 197 94 / 0.95)',
  removed: 'rgb(239 68 68 / 0.95)',
  both: 'rgb(234 179 8 / 0.95)',
}

export interface DiffOverviewRulerProps {
  scrollRef: React.RefObject<HTMLElement | null>
  markers: DiffOverviewMarker[]
}

/**
 * VS Code-style diff overview rail: always-visible change stripes plus viewport indicator.
 */
export function DiffOverviewRuler({ scrollRef, markers }: DiffOverviewRulerProps) {
  const rulerRef = useRef<HTMLDivElement>(null)
  const vpRef = useRef<HTMLDivElement>(null)
  const scrollStateRef = useRef({ scrollTop: 0, scrollHeight: 1, clientHeight: 1 })
  const draggingRef = useRef(false)
  const dragOffsetRef = useRef(0)

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    const update = () => {
      const { scrollTop, scrollHeight, clientHeight } = el
      scrollStateRef.current = { scrollTop, scrollHeight, clientHeight }
      const scrollable = scrollHeight - clientHeight
      const vpTop = scrollable > 0 ? scrollTop / scrollHeight : 0
      const vpHeight = clientHeight / scrollHeight
      if (vpRef.current) {
        vpRef.current.style.top = `${vpTop * 100}%`
        vpRef.current.style.height = `${Math.max(vpHeight * 100, 2)}%`
      }
    }

    update()
    el.addEventListener('scroll', update, { passive: true })
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => {
      el.removeEventListener('scroll', update)
      ro.disconnect()
    }
  }, [scrollRef, markers.length])

  useEffect(() => {
    const ruler = rulerRef.current
    const el = scrollRef.current
    if (!ruler || !el) return

    const onMouseDown = (e: MouseEvent) => {
      e.preventDefault()
      const rect = ruler.getBoundingClientRect()
      const { scrollHeight, clientHeight } = scrollStateRef.current
      const scrollable = scrollHeight - clientHeight
      const vpTop = scrollable > 0 ? el.scrollTop / scrollHeight : 0
      const vpHeight = clientHeight / scrollHeight
      const clickRatio = (e.clientY - rect.top) / rect.height
      const isOnViewport = clickRatio >= vpTop && clickRatio <= vpTop + vpHeight

      draggingRef.current = true
      if (isOnViewport) {
        dragOffsetRef.current = clickRatio - vpTop
      } else {
        const targetRatio = Math.max(0, Math.min(1 - vpHeight, clickRatio - vpHeight / 2))
        el.scrollTop = targetRatio * scrollHeight
        dragOffsetRef.current = vpHeight / 2
      }

      const onMove = (moveEvent: MouseEvent) => {
        if (!draggingRef.current) return
        const { scrollHeight: sh, clientHeight: ch } = scrollStateRef.current
        const nextVpHeight = ch / sh
        const nextRect = ruler.getBoundingClientRect()
        const nextClickRatio = (moveEvent.clientY - nextRect.top) / nextRect.height
        el.scrollTop =
          Math.max(0, Math.min(1 - nextVpHeight, nextClickRatio - dragOffsetRef.current)) * sh
      }

      const onUp = () => {
        draggingRef.current = false
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)
      }

      window.addEventListener('mousemove', onMove)
      window.addEventListener('mouseup', onUp)
    }

    ruler.addEventListener('mousedown', onMouseDown)
    return () => ruler.removeEventListener('mousedown', onMouseDown)
  }, [scrollRef, markers.length])

  const stripeHeightPct = Math.max(0.35, Math.min(1.2, 120 / Math.max(markers.length, 1)))

  return (
    <div
      ref={rulerRef}
      role="scrollbar"
      aria-orientation="vertical"
      aria-label="Diff change overview"
      data-testid={testIds.diffOverview.ruler}
      className="absolute right-0 top-0 bottom-0 z-20 w-3 select-none cursor-pointer border-l border-border/60 bg-muted/40"
      title={markers.length > 0 ? `${markers.length} changed region${markers.length === 1 ? '' : 's'} - click to scroll` : undefined}
    >
      {markers.map((marker, idx) => (
        <div
          key={`${marker.type}-${marker.ratio}-${idx}`}
          className="absolute left-0.5 right-0.5 rounded-[1px] pointer-events-none"
          style={{
            top: `${marker.ratio * 100}%`,
            height: `${stripeHeightPct}%`,
            minHeight: 3,
            transform: 'translateY(-50%)',
            background: MARKER_COLORS[marker.type],
            boxShadow: '0 0 0 1px rgb(0 0 0 / 0.08)',
          }}
        />
      ))}

      <div
        ref={vpRef}
        className="absolute pointer-events-none"
        style={{
          left: 1,
          right: 1,
          minHeight: 4,
          background: 'hsl(var(--primary) / 0.14)',
          border: '1px solid hsl(var(--primary) / 0.35)',
          borderRadius: 2,
          transition: 'top 0.05s, height 0.05s',
        }}
      />
    </div>
  )
}
